import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { ActivityIndicator } from 'components/BlockingLoader/ActivityIndicator';
import { Button } from 'components/Button';
import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { ICONS } from 'components/Icon';
import {
  ModalScreens,
  NavigationStacks,
  Screens,
  Stacks,
} from 'components/Navigation/types';
import { Page } from 'components/Page';
import { PageEmpty } from 'components/PageEmpty';
import { PageHeader } from 'components/PageHeader';
import { useScreenSize } from 'hooks/useScreenSize';
import { ScreenSize } from 'lib/constants';
import { pluraliseS } from 'lib/pluralise';
import { theme } from 'lib/theme';
import { sortBy } from 'lodash';
import React from 'react';
import { View } from 'react-native';
import { useSelector, useDispatch } from 'react-redux';
import {
  doSettingsSeveritiesDelete,
  doSettingsSeveritiesLoad,
  doSettingsSeveritiesReorder,
  NAVIGATE_MODAL,
} from 'store/actions';
import { selectUserSelectedOrganisation } from 'store/auth/selectors';
import {
  selectRemoteSettingsSeverities,
  selectRemoteSettingsSeveritiesLoadStatus,
  selectRemoteSettingsSeveritiesManagedByOrganisation,
  selectRemoteSettingsSeveritiesSharedWithOrganisations,
} from 'store/remote/selectors';

type Props = NativeStackScreenProps<
  NavigationStacks[Stacks.SETTINGS],
  Screens.SettingsSeverities
>;

export const SettingsSeveritiesScreen: React.FC<Props> = ({ navigation }) => {
  const screenSize = useScreenSize();
  const dispatch = useDispatch();

  const organisation = useSelector(selectUserSelectedOrganisation)!;

  const severities = useSelector(selectRemoteSettingsSeverities)!;
  const loadStatus = useSelector(selectRemoteSettingsSeveritiesLoadStatus);
  const sharedWithOrganisations = useSelector(
    selectRemoteSettingsSeveritiesSharedWithOrganisations,
  );
  const managedByOrganisation = useSelector(
    selectRemoteSettingsSeveritiesManagedByOrganisation,
  );

  const sortedSeverities = React.useMemo(
    () => sortBy(Object.values(severities || {}), 'position'),
    [severities],
  );

  React.useEffect(() => {
    dispatch(
      doSettingsSeveritiesLoad({
        organisationId: organisation.id,
      }),
    );
  }, [dispatch, organisation]);

  const openSeverityModal = (severityId: string | null) =>
    dispatch(
      NAVIGATE_MODAL({
        screen: ModalScreens.SettingsSeverityModal,
        params: {
          // Severities, and hence organisations, must be loaded for openSeverityModal to be called.
          // Fallback is only included for type safety
          organisationId: organisation.id,
          severityId: severityId,
        },
      }),
    );

  const openReorderModal = () =>
    dispatch(
      NAVIGATE_MODAL({
        screen: ModalScreens.REORDER,
        params: {
          title: 'Reorder Task Severities',
          action: doSettingsSeveritiesReorder({
            organisationId: organisation.id,
            order: [],
          }),
          options: sortedSeverities.map((sev) => ({
            name: sev.name,
            value: sev.id,
          })),
          closeOnDone: true,
        },
      }),
    );

  const openSharedWithModal = () =>
    dispatch(
      NAVIGATE_MODAL({
        screen: ModalScreens.ListDisplay,
        params: {
          title: 'Shared Organisations',
          text: 'Task severities are shared with the following organisations.',
          cardLabel: 'Organisations',
          emptyText: 'No Organisations',
          rows: sortBy(sharedWithOrganisations || [], 'name').map((org) => ({
            key: org.id,
            label: org.name,
          })),
        },
      }),
    );

  return (
    <Page
      scrollView
      header={
        <PageHeader
          title="Task Severities"
          onPressBackButton={
            screenSize === ScreenSize.small ? navigation.goBack : undefined
          }
          onPressSaveButton={
            !managedByOrganisation ? () => openSeverityModal(null) : undefined
          }
          saveButtonIcon={ICONS.PlusLight}
        />
      }
    >
      {sharedWithOrganisations && sharedWithOrganisations?.length > 0 && (
        <Button
          onPress={openSharedWithModal}
          marginLeft
          marginRight
          style={{ alignSelf: 'flex-start' }}
        >{`These severities are shared with ${pluraliseS(sharedWithOrganisations.length, 'organisation')}`}</Button>
      )}
      {loadStatus === 'loaded' && (
        <>
          {!!managedByOrganisation && (
            <Card
              explanation={`These task severities are managed by ${managedByOrganisation.name}.`}
            ></Card>
          )}

          {/* Null (No Severity) */}
          <Card>
            <CardRow
              icon={ICONS.CircleSolid}
              iconColor={theme.colors.black20}
              label="No Severity"
              label2="This is a default severity and cannot be changed"
              disabled={!!managedByOrganisation}
            />
          </Card>

          {/* Set Organisation Values */}
          <Card>
            {!!severities
              ? sortedSeverities.map((severity) => {
                  return (
                    <CardRow
                      key={severity.id}
                      icon={ICONS.CircleSolid}
                      iconColor={severity.color}
                      label={severity.name}
                      label2={severity.description}
                      label2NumberOfLines={2}
                      onPress={() => openSeverityModal(severity.id)}
                      menuOptions={[
                        {
                          text: 'Reorder Severities',
                          onPress: openReorderModal,
                        },
                        {
                          text: 'Delete Severity',
                          onPress: () =>
                            dispatch(
                              doSettingsSeveritiesDelete({
                                organisationId: organisation.id,
                                severityId: severity.id,
                              }),
                            ),
                        },
                      ]}
                      disabled={!!managedByOrganisation}
                      hideMenuButton={!!managedByOrganisation}
                    />
                  );
                })
              : null}
          </Card>
        </>
      )}
      {loadStatus === 'loading' && (
        <View
          style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}
        >
          <ActivityIndicator size={56} />
        </View>
      )}
      {loadStatus === 'error' && (
        <PageEmpty
          icon={ICONS.ExclamationSolid}
          text="Unable to Load Task Severities"
        />
      )}
    </Page>
  );
};
